import { Link } from "react-router-dom";
import type { IncidentSeverity, PublicIncidentView, PublicPlatformComponent } from "../lib/api";
import { usePublicStatus } from "../hooks/usePublicStatus";
import { useSession } from "../hooks/useSession";

/**
 * Dashboard incident + degraded-state banner (Phase 8, section 28).
 *
 * Surfaces active PUBLIC incidents and coarse degraded states — maintenance,
 * deployment freeze, n8n / object-storage (R2) degradation, and session
 * expiration. It renders ONLY the secret-free public projection: no tenant name,
 * guild id, hostname, queue name or stack trace ever appears here.
 */

export type BannerVariant = "info" | "accent" | "warn" | "danger";

export interface BannerItem {
  id: string;
  variant: BannerVariant;
  label: string;
  message: string;
  action?: { to: string; label: string };
}

const SEVERITY_VARIANT: Record<IncidentSeverity, BannerVariant> = {
  INFORMATION: "info",
  MAINTENANCE: "accent",
  DEGRADED: "warn",
  PARTIAL_OUTAGE: "warn",
  MAJOR_OUTAGE: "danger",
};

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  INFORMATION: "Information",
  MAINTENANCE: "Maintenance",
  DEGRADED: "Degraded",
  PARTIAL_OUTAGE: "Partial outage",
  MAJOR_OUTAGE: "Major outage",
};

const COMPONENT_LABEL: Record<string, string> = {
  "object-storage": "Object storage (R2)",
  n8n: "Automation (n8n)",
  "control-api": "Control API",
  "bot-runtime": "Bot runtime",
  worker: "Background worker",
  database: "Database",
  dashboard: "Dashboard",
  cache: "Cache",
};

function componentLabel(name: string): string {
  return COMPONENT_LABEL[name] ?? name;
}

export interface IncidentBannerInputs {
  incidents?: PublicIncidentView[];
  components?: PublicPlatformComponent[];
  /** Live deployment is frozen (deployments are held). */
  deploymentFreeze?: boolean;
  /** A previously authenticated session has expired / is no longer valid. */
  sessionExpired?: boolean;
}

/** Pure derivation of banner items from the safe public inputs. Exported for tests. */
export function deriveBanners(inputs: IncidentBannerInputs): BannerItem[] {
  const items: BannerItem[] = [];

  if (inputs.sessionExpired) {
    items.push({
      id: "session-expired",
      variant: "warn",
      label: "Session expired",
      message: "Your session has expired. Sign in again to continue.",
      action: { to: "/login", label: "Sign in" },
    });
  }

  for (const incident of inputs.incidents ?? []) {
    if (incident.state === "RESOLVED") continue;
    const affected = incident.components.map(componentLabel).join(", ");
    items.push({
      id: `incident-${incident.title}-${incident.startedAtIso}`,
      variant: SEVERITY_VARIANT[incident.severity],
      label: SEVERITY_LABEL[incident.severity],
      message: affected ? `${incident.title} — affects ${affected}.` : incident.title,
      action: { to: "/status", label: "View status" },
    });
  }

  // Degraded components not already covered by a public incident.
  const incidentComponents = new Set((inputs.incidents ?? []).flatMap((i) => i.components));
  for (const component of inputs.components ?? []) {
    if (component.state !== "degraded") continue;
    if (incidentComponents.has(component.name as never)) continue;
    items.push({
      id: `component-${component.name}`,
      variant: "warn",
      label: "Degraded",
      message: `${componentLabel(component.name)} is currently degraded. Some actions may be delayed.`,
      action: { to: "/status", label: "View status" },
    });
  }

  if (inputs.deploymentFreeze) {
    items.push({
      id: "deployment-freeze",
      variant: "accent",
      label: "Deployment freeze",
      message: "Live deployments are frozen right now. You can still design and dry-run safely.",
    });
  }

  return items;
}

export function IncidentBannerView({ items }: { items: BannerItem[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="banner-stack" role="region" aria-label="Platform notices">
      {items.map((item) => (
        <div key={item.id} className={`banner-item banner-item--${item.variant}`} role="alert">
          <span className="banner-item__label">{item.label}</span>
          <span className="banner-item__message">{item.message}</span>
          {item.action ? (
            <Link className="banner-item__action" to={item.action.to}>
              {item.action.label}
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Container wired into the authenticated layout. Reads the public status and the
 * session state, then renders derived banners. Fails silent (renders nothing) when
 * status is loading, unavailable, or unconfigured — a missing status source must
 * never block the dashboard.
 */
export function IncidentBanner(): JSX.Element | null {
  const { state } = usePublicStatus();
  const { state: session } = useSession();

  const ready = state.kind === "ready" ? state.data : null;
  // A permanently "locked (safe)" platform is the normal state, so we do NOT show a
  // freeze banner for it. A deliberate freeze is surfaced as a MAINTENANCE incident.
  const items = deriveBanners({
    incidents: ready?.incidents,
    components: ready?.components,
    sessionExpired: session.kind === "anonymous",
  });

  return <IncidentBannerView items={items} />;
}
